import { Button, Dialog, Switch } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import {
  assignCheck,
  listPlanChecks,
  type PlanCheck,
} from "../setup/checklist-api.js";
import { personCheckChange } from "../setup/setup-checklist-model.js";
import type { StaffProfile } from "../setup/setup-api.js";

// The checks one person does, one switch each, for the setup visit: a
// bartender is switched off the fridges.
export function PersonChecksDialog({
  locationId,
  onClose,
  person,
}: {
  locationId: string;
  onClose: () => void;
  person: StaffProfile;
}) {
  const queryClient = useQueryClient();
  const queryKey = ["plan-checks", locationId];
  const checks = useQuery({
    queryFn: () => listPlanChecks(locationId),
    queryKey,
  });
  const toggle = useMutation({
    mutationFn: ({ check, on }: { check: PlanCheck; on: boolean }) =>
      assignCheck(personCheckChange(check, person.id, on)),
    onSuccess: async (saved) => {
      queryClient.setQueryData<PlanCheck[]>(queryKey, (current) =>
        current?.map((check) => (check.id === saved.id ? saved : check)),
      );
      await queryClient.invalidateQueries({ queryKey: ["task-definitions"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
  const sorted = [...(checks.data ?? [])].sort((left, right) =>
    left.title.localeCompare(right.title),
  );

  return (
    <Dialog
      footer={<Button onClick={onClose}>Done</Button>}
      onClose={onClose}
      open
      title={`${person.display_name}’s checks`}
    >
      {checks.isPending ? <LoadingState label="Loading checks" /> : null}
      {checks.error ? (
        <ErrorState
          message={displayError(checks.error)}
          onAction={() => void checks.refetch()}
          title="Couldn’t load checks"
        />
      ) : null}
      {checks.data?.length === 0 ? <EmptyState title="No checks yet" /> : null}
      {sorted.length > 0 ? (
        <div className="-my-1 divide-y divide-stone-100">
          {sorted.map((check) => (
            <Switch
              checked={check.assignee_staff_profile_ids.includes(person.id)}
              disabled={
                toggle.isPending && toggle.variables?.check.id === check.id
              }
              key={check.id}
              label={check.title}
              onCheckedChange={(on) => toggle.mutate({ check, on })}
            />
          ))}
        </div>
      ) : null}
      {toggle.error ? (
        <p className="mt-3 text-sm font-semibold text-red-800" role="alert">
          {displayError(toggle.error)}
        </p>
      ) : null}
    </Dialog>
  );
}
